import { cn } from "@/lib/utils";
import type { EmulatorSessionDTO } from "@mm-sales/shared";
import JMuxer from "jmuxer";
import { useEffect, useRef, useState } from "react";

/**
 * The register's customer-facing display: the same emulator's second screen, captured by
 * the broker with upstream scrcpy and relayed as H.264 packets over /cfd/<slot>/ (proxied
 * by the api). jmuxer wraps the raw NAL units into fragmented MP4 for Media Source
 * Extensions, so it plays in a plain <video> with ~0.2 s of delay. Reconnects while mounted.
 */
export function CfdVideo({
  session,
  className,
}: {
  session: EmulatorSessionDTO | null;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<"connecting" | "live" | "ended">("connecting");
  const slot = session?.slot ?? null;
  const displayId = session?.cfdDisplayId ?? null;
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !slot || displayId === null) return;
    let stopped = false;
    let ws: WebSocket | null = null;
    let mux: JMuxer | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    const connect = () => {
      if (stopped) return;
      setState("connecting");
      mux?.destroy();
      mux = new JMuxer({
        node: video,
        mode: "video",
        flushingTime: 0,
        fps: 15,
        clearBuffer: true,
        debug: false,
        onError: () => {
          /* a bad chunk is dropped; the next key frame recovers */
        },
      });
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(`${proto}//${window.location.host}/cfd/${slot}/`);
      ws.binaryType = "arraybuffer";
      let first = true;
      ws.onmessage = (e) => {
        mux?.feed({ video: new Uint8Array(e.data as ArrayBuffer) });
        if (first) {
          first = false;
          setState("live");
          void video.play().catch(() => {});
        }
      };
      ws.onclose = () => {
        if (stopped) return;
        setState("ended");
        retry = setTimeout(connect, 2500);
      };
      ws.onerror = () => ws?.close();
    };
    connect();
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      ws?.close();
      mux?.destroy();
    };
  }, [slot, displayId]);
  return (
    <div className={cn("relative h-full w-full bg-black", className)}>
      <video ref={videoRef} autoPlay muted playsInline className="h-full w-full object-contain" />
      {state !== "live" ? (
        <div className="absolute inset-0 flex items-center justify-center text-[12px] text-neutral-400">
          {state === "connecting"
            ? "Connecting to the customer display…"
            : "Customer display stream ended — reconnecting…"}
        </div>
      ) : null}
    </div>
  );
}
